import React, { useCallback, useMemo, useState } from 'react'
import AppDropdown from '../../../../components/AppDropdown'
import AppCombobox from '../../../../components/AppCombobox'
import {
  StepHeader,
  StepCard,
  INPUT_CLASS,
  DROPDOWN_TRIGGER_CLASS,
  LABEL_CLASS,
} from '../components/StepLayout'

const CALC_FORMAT_OPTIONS = [
  { value: 'number', label: 'Number (Decimal)' },
  { value: 'currency', label: 'Currency (₹)' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'integer', label: 'Integer' },
]

const ARITHMETIC_TYPES = ['number', 'currency', 'percentage']

const OPERATORS = [
  { value: '+', label: '+', title: 'Add' },
  { value: '-', label: '−', title: 'Subtract' },
  { value: '*', label: '×', title: 'Multiply' },
  { value: '/', label: '÷', title: 'Divide' },
]

let tokenSeq = 0
const nextTokenId = () => `tok_${Date.now()}_${tokenSeq++}`

function fieldLabel(allFields, key) {
  const f = allFields.find((x) => x.key === key)
  return f ? f.label : key
}

/**
 * Turns a stored expression string (e.g. "{a} - {b}") back into editable
 * chips. Returns null when the formula uses syntax the visual builder can't
 * safely round-trip, so the caller can fall back to raw-text editing instead
 * of silently mangling it.
 */
function tokenizeExpression(expression) {
  const text = (expression || '').trim()
  if (!text) return []

  const pattern =
    /NULLIF\(\{([a-zA-Z0-9_]+)\},\s*0\)|DATEDIFF\(day,\s*\{([a-zA-Z0-9_]+)\},\s*GETDATE\(\)\)|DATEDIFF\(day,\s*\{([a-zA-Z0-9_]+)\},\s*\{([a-zA-Z0-9_]+)\}\)|\{([a-zA-Z0-9_]+)\}|\(|\)|\+|-|\*|\/|\d+(?:\.\d+)?/g

  const tokens = []
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = pattern.exec(text)) !== null) {
    const [whole, nullifField, daysSinceField, daysBetweenA, daysBetweenB, fieldKey] = match
    if (nullifField) {
      tokens.push({ id: nextTokenId(), type: 'safe-divide', field: nullifField })
    } else if (daysSinceField) {
      tokens.push({ id: nextTokenId(), type: 'days-since', field: daysSinceField })
    } else if (daysBetweenA) {
      tokens.push({ id: nextTokenId(), type: 'days-between', from: daysBetweenA, to: daysBetweenB })
    } else if (fieldKey) {
      tokens.push({ id: nextTokenId(), type: 'field', field: fieldKey })
    } else if (whole === '(' || whole === ')') {
      tokens.push({ id: nextTokenId(), type: 'paren', value: whole })
    } else if (['+', '-', '*', '/'].includes(whole)) {
      tokens.push({ id: nextTokenId(), type: 'operator', value: whole })
    } else {
      tokens.push({ id: nextTokenId(), type: 'number', value: whole })
    }
  }

  // Safety check: re-serialize and compare (ignoring whitespace) to make sure
  // nothing was silently dropped by the regex (e.g. an unsupported function).
  const collapsedOriginal = text.replace(/\s+/g, '')
  const rebuilt = serializeTokens(tokens).replace(/\s+/g, '')
  if (collapsedOriginal !== rebuilt) return null
  return tokens
}

function serializeTokens(tokens) {
  return tokens
    .map((t) => {
      if (t.type === 'field') return `{${t.field}}`
      if (t.type === 'safe-divide') return `NULLIF({${t.field}}, 0)`
      if (t.type === 'days-since') return `DATEDIFF(day, {${t.field}}, GETDATE())`
      if (t.type === 'days-between') return `DATEDIFF(day, {${t.from}}, {${t.to}})`
      return t.value
    })
    .join(' ')
}

function humanPreview(tokens, allFields) {
  if (!tokens.length) return ''
  return tokens
    .map((t) => {
      if (t.type === 'field') return fieldLabel(allFields, t.field)
      if (t.type === 'safe-divide') return `${fieldLabel(allFields, t.field)} (safely)`
      if (t.type === 'days-since') return `days since ${fieldLabel(allFields, t.field)}`
      if (t.type === 'days-between') return `days between ${fieldLabel(allFields, t.from)} & ${fieldLabel(allFields, t.to)}`
      if (t.type === 'paren') return t.value
      if (t.type === 'operator') return OPERATORS.find((o) => o.value === t.value)?.label || t.value
      return t.value
    })
    .join('  ')
}

/**
 * A small state machine that walks the token list the same way a formula
 * parser would, so we can catch things like "two operators in a row" or
 * "unbalanced parentheses" before the person ever hits save.
 */
function validateTokens(tokens) {
  if (tokens.length === 0) return 'Add at least one field to build a formula.'

  let depth = 0
  let expect = 'operand' // 'operand' | 'operator'

  for (const t of tokens) {
    const isOperator = t.type === 'operator'
    const isOpenParen = t.type === 'paren' && t.value === '('
    const isCloseParen = t.type === 'paren' && t.value === ')'
    const isValue = !isOperator && !isOpenParen && !isCloseParen

    if (expect === 'operand') {
      if (isOperator) return 'A formula cannot start with an operator, or have two in a row.'
      if (isCloseParen) return 'Unexpected closing parenthesis.'
      if (isOpenParen) {
        depth += 1
        continue
      }
      if (isValue) {
        expect = 'operator'
        continue
      }
    } else {
      if (isOperator) {
        expect = 'operand'
        continue
      }
      if (isCloseParen) {
        depth -= 1
        if (depth < 0) return 'Closing parenthesis without a matching opening one.'
        continue
      }
      // an open-paren or another value showing up where an operator was expected
      return 'Add an operator (+, −, ×, ÷) between these two parts.'
    }
  }

  if (depth !== 0) return 'Parentheses are not balanced — add the missing one.'
  if (expect === 'operand') return 'The formula ends without a value — remove the trailing operator.'
  return null
}

function TokenChip({ token, allFields, onRemove, onEdit }) {
  const editable = token.type === 'safe-divide' || token.type === 'days-since' || token.type === 'days-between'
  let text = ''
  let toneClass = 'bg-surface-container-high text-on-surface'
  let icon = null

  if (token.type === 'field') {
    text = fieldLabel(allFields, token.field)
    toneClass = 'bg-primary/10 text-primary font-semibold'
    icon = 'view_column'
  } else if (token.type === 'operator') {
    text = OPERATORS.find((o) => o.value === token.value)?.label || token.value
    toneClass = 'bg-surface-container-highest text-on-surface font-bold'
  } else if (token.type === 'paren') {
    text = token.value
    toneClass = 'bg-transparent text-on-surface-variant font-bold text-sm'
  } else if (token.type === 'number') {
    text = token.value
    toneClass = 'bg-secondary/10 text-secondary font-semibold'
    icon = 'tag'
  } else if (token.type === 'safe-divide') {
    text = `÷ ${fieldLabel(allFields, token.field)}`
    toneClass = 'bg-emerald-500/10 text-emerald-700 font-semibold'
    icon = 'shield'
  } else if (token.type === 'days-since') {
    text = `Days since ${fieldLabel(allFields, token.field)}`
    toneClass = 'bg-amber-500/10 text-amber-700 font-semibold'
    icon = 'event'
  } else if (token.type === 'days-between') {
    text = `Days: ${fieldLabel(allFields, token.from)} → ${fieldLabel(allFields, token.to)}`
    toneClass = 'bg-amber-500/10 text-amber-700 font-semibold'
    icon = 'date_range'
  }

  return (
    <span
      className={`group inline-flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-1 text-xs ${toneClass} ${
        editable ? 'cursor-pointer' : ''
      }`}
      onClick={editable ? onEdit : undefined}
      title={editable ? 'Click to edit' : undefined}
    >
      {icon && <span className="material-symbols-outlined text-sm">{icon}</span>}
      <span>{text}</span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-current hover:bg-black/10 transition-colors"
        title="Remove"
      >
        <span className="material-symbols-outlined text-[13px]">close</span>
      </button>
    </span>
  )
}

function FormulaBuilder({ calc, allFields, onChangeExpression }) {
  const [mode, setMode] = useState(() => (tokenizeExpression(calc.expression) !== null ? 'visual' : 'text'))
  const [tokens, setTokens] = useState(() => tokenizeExpression(calc.expression) || [])
  const [numberDraft, setNumberDraft] = useState('')
  const [activePanel, setActivePanel] = useState(null) // 'safe-divide' | 'days-since' | 'days-between' | null
  const [editingIndex, setEditingIndex] = useState(null)
  const [panelFieldA, setPanelFieldA] = useState('')
  const [panelFieldB, setPanelFieldB] = useState('')

  const arithmeticFields = useMemo(() => allFields.filter((f) => ARITHMETIC_TYPES.includes(f.dataType)), [allFields])
  const dateFields = useMemo(() => allFields.filter((f) => f.dataType === 'date'), [allFields])

  const commit = useCallback(
    (nextTokens) => {
      setTokens(nextTokens)
      onChangeExpression(serializeTokens(nextTokens))
    },
    [onChangeExpression]
  )

  function addTokens(newTokens) {
    commit([...tokens, ...newTokens])
  }
  function removeAt(index) {
    commit(tokens.filter((_, i) => i !== index))
  }
  function undoLast() {
    commit(tokens.slice(0, -1))
  }
  function clearAll() {
    commit([])
  }

  function openPanel(kind, editIndex = null) {
    setActivePanel(kind)
    setEditingIndex(editIndex)
    if (editIndex !== null) {
      const t = tokens[editIndex]
      setPanelFieldA(t.field || t.from || '')
      setPanelFieldB(t.to || '')
    } else {
      setPanelFieldA('')
      setPanelFieldB('')
    }
  }
  function closePanel() {
    setActivePanel(null)
    setEditingIndex(null)
    setPanelFieldA('')
    setPanelFieldB('')
  }
  function confirmPanel() {
    if (!panelFieldA) return
    let token = null
    if (activePanel === 'safe-divide') token = { id: nextTokenId(), type: 'safe-divide', field: panelFieldA }
    if (activePanel === 'days-since') token = { id: nextTokenId(), type: 'days-since', field: panelFieldA }
    if (activePanel === 'days-between') {
      if (!panelFieldB) return
      token = { id: nextTokenId(), type: 'days-between', from: panelFieldA, to: panelFieldB }
    }
    if (!token) return
    if (editingIndex !== null) {
      const next = [...tokens]
      next[editingIndex] = token
      commit(next)
    } else {
      commit([...tokens, token])
    }
    closePanel()
  }

  function switchToVisual() {
    const parsed = tokenizeExpression(calc.expression)
    if (parsed !== null) {
      setTokens(parsed)
      setMode('visual')
    }
  }

  const validationError = mode === 'visual' ? validateTokens(tokens) : null
  const preview = mode === 'visual' ? humanPreview(tokens, allFields) : ''

  if (mode === 'text') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className={LABEL_CLASS}>Formula (typed)</span>
          <button type="button" onClick={switchToVisual} className="text-[11px] font-bold text-primary hover:underline">
            Switch to visual builder
          </button>
        </div>
        <input
          type="text"
          value={calc.expression}
          onChange={(e) => onChangeExpression(e.target.value)}
          placeholder="e.g., ({financial_revenue} - {financial_cost}) * 100"
          className="h-10 w-full font-mono text-sm px-3 rounded-[8px] border border-surface-container-high bg-surface-container-lowest text-on-surface placeholder:text-on-surface-variant/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <span className={LABEL_CLASS}>Formula (no typing needed)</span>
        <button
          type="button"
          onClick={() => setMode('text')}
          className="text-[11px] font-bold text-on-surface-variant hover:text-primary hover:underline"
        >
          Type it instead
        </button>
      </div>

      {/* Token strip */}
      <div className="min-h-[46px] flex flex-wrap items-center gap-1.5 p-2.5 rounded-[8px] border border-dashed border-surface-container-high bg-surface">
        {tokens.length === 0 ? (
          <span className="text-xs text-on-surface-variant/70 italic px-1">
            Nothing yet — add a field below to start building your formula.
          </span>
        ) : (
          tokens.map((t, i) => (
            <TokenChip
              key={t.id}
              token={t}
              allFields={allFields}
              onRemove={() => removeAt(i)}
              onEdit={
                t.type === 'safe-divide'
                  ? () => openPanel('safe-divide', i)
                  : t.type === 'days-since'
                  ? () => openPanel('days-since', i)
                  : t.type === 'days-between'
                  ? () => openPanel('days-between', i)
                  : undefined
              }
            />
          ))
        )}
      </div>

      {/* Preview + validation */}
      {tokens.length > 0 && (
        <div
          className={`flex items-start gap-1.5 rounded-[8px] px-2.5 py-2 text-xs ${
            validationError ? 'bg-error-container/60 text-on-error-container' : 'bg-emerald-500/10 text-emerald-800'
          }`}
        >
          <span className="material-symbols-outlined text-sm shrink-0">{validationError ? 'error' : 'check_circle'}</span>
          <span>{validationError || `Reads as: ${preview}`}</span>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5">
        <div className="w-48">
          <AppCombobox
            options={arithmeticFields.map((f) => ({ value: f.key, label: f.label }))}
            value=""
            onChange={(val) => val && addTokens([{ id: nextTokenId(), type: 'field', field: val }])}
            placeholder="+ Add a field"
            searchPlaceholder="Search fields..."
            allowClear={false}
            triggerClassName="h-9 bg-surface text-xs font-bold"
          />
        </div>

        <div className="flex items-center gap-1 rounded-[8px] border border-surface-container-high p-0.5 bg-surface">
          {OPERATORS.map((op) => (
            <button
              key={op.value}
              type="button"
              title={op.title}
              onClick={() => addTokens([{ id: nextTokenId(), type: 'operator', value: op.value }])}
              className="h-8 w-8 rounded-[6px] text-sm font-bold text-on-surface hover:bg-primary/10 hover:text-primary transition-colors"
            >
              {op.label}
            </button>
          ))}
          <span className="mx-0.5 h-5 w-px bg-surface-container-high" />
          <button
            type="button"
            title="Open parenthesis"
            onClick={() => addTokens([{ id: nextTokenId(), type: 'paren', value: '(' }])}
            className="h-8 w-8 rounded-[6px] text-sm font-bold text-on-surface-variant hover:bg-surface-container-high"
          >
            (
          </button>
          <button
            type="button"
            title="Close parenthesis"
            onClick={() => addTokens([{ id: nextTokenId(), type: 'paren', value: ')' }])}
            className="h-8 w-8 rounded-[6px] text-sm font-bold text-on-surface-variant hover:bg-surface-container-high"
          >
            )
          </button>
        </div>

        <div className="flex items-center gap-1">
          <input
            type="number"
            value={numberDraft}
            onChange={(e) => setNumberDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && numberDraft.trim() !== '') {
                addTokens([{ id: nextTokenId(), type: 'number', value: numberDraft.trim() }])
                setNumberDraft('')
              }
            }}
            placeholder="Number"
            className="h-9 w-20 rounded-[8px] border border-surface-container-high bg-surface px-2 text-xs text-on-surface focus:border-primary focus:outline-none"
          />
          <button
            type="button"
            disabled={numberDraft.trim() === ''}
            onClick={() => {
              addTokens([{ id: nextTokenId(), type: 'number', value: numberDraft.trim() }])
              setNumberDraft('')
            }}
            className="h-9 px-2.5 rounded-[8px] border border-surface-container-high bg-surface text-xs font-bold text-on-surface-variant hover:bg-surface-container disabled:opacity-40"
          >
            Add
          </button>
        </div>

        <button
          type="button"
          onClick={() =>
            addTokens([
              { id: nextTokenId(), type: 'operator', value: '*' },
              { id: nextTokenId(), type: 'number', value: '100' },
            ])
          }
          className="h-9 px-2.5 rounded-[8px] border border-surface-container-high bg-surface text-xs font-bold text-on-surface-variant hover:bg-surface-container"
          title="Adds × 100 — handy for turning a ratio into a percentage"
        >
          × 100 (%)
        </button>

        <button
          type="button"
          onClick={() => openPanel('safe-divide')}
          className="h-9 flex items-center gap-1 px-2.5 rounded-[8px] border border-emerald-500/30 bg-emerald-500/5 text-xs font-bold text-emerald-700 hover:bg-emerald-500/10"
          title="Divide by a field, avoiding a divide-by-zero error"
        >
          <span className="material-symbols-outlined text-sm">shield</span>
          ÷ Safe divide
        </button>

        <button
          type="button"
          onClick={() => openPanel('days-since')}
          className="h-9 flex items-center gap-1 px-2.5 rounded-[8px] border border-amber-500/30 bg-amber-500/5 text-xs font-bold text-amber-700 hover:bg-amber-500/10"
          title="Insert 'days since a date field'"
        >
          <span className="material-symbols-outlined text-sm">event</span>
          Days since...
        </button>

        <button
          type="button"
          onClick={() => openPanel('days-between')}
          className="h-9 flex items-center gap-1 px-2.5 rounded-[8px] border border-amber-500/30 bg-amber-500/5 text-xs font-bold text-amber-700 hover:bg-amber-500/10"
          title="Insert 'days between two date fields'"
        >
          <span className="material-symbols-outlined text-sm">date_range</span>
          Days between...
        </button>

        <span className="flex-1" />

        <button
          type="button"
          disabled={tokens.length === 0}
          onClick={undoLast}
          className="h-9 w-9 flex items-center justify-center rounded-[8px] text-on-surface-variant hover:bg-surface-container disabled:opacity-30"
          title="Remove last piece"
        >
          <span className="material-symbols-outlined text-base">backspace</span>
        </button>
        <button
          type="button"
          disabled={tokens.length === 0}
          onClick={clearAll}
          className="h-9 w-9 flex items-center justify-center rounded-[8px] text-on-surface-variant hover:bg-surface-container disabled:opacity-30"
          title="Clear formula"
        >
          <span className="material-symbols-outlined text-base">delete_sweep</span>
        </button>
      </div>

      {/* Inline mini-panels for composite tokens */}
      {activePanel && (
        <div className="flex flex-wrap items-end gap-2 p-3 rounded-[8px] bg-surface-container-low border border-surface-container-high">
          {activePanel === 'safe-divide' && (
            <>
              <div className="w-48">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase block mb-1">Divide by</span>
                <AppDropdown
                  value={panelFieldA}
                  onChange={setPanelFieldA}
                  options={arithmeticFields.map((f) => ({ value: f.key, label: f.label }))}
                  placeholder="Choose a field..."
                  triggerClassName="h-9 bg-surface text-xs"
                />
              </div>
              <p className="text-[10px] text-on-surface-variant max-w-[220px] pb-2">
                Returns blank instead of an error when this field is zero.
              </p>
            </>
          )}
          {activePanel === 'days-since' && (
            <div className="w-48">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase block mb-1">From date field</span>
              <AppDropdown
                value={panelFieldA}
                onChange={setPanelFieldA}
                options={dateFields.map((f) => ({ value: f.key, label: f.label }))}
                placeholder="Choose a date field..."
                triggerClassName="h-9 bg-surface text-xs"
              />
            </div>
          )}
          {activePanel === 'days-between' && (
            <>
              <div className="w-44">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase block mb-1">From</span>
                <AppDropdown
                  value={panelFieldA}
                  onChange={setPanelFieldA}
                  options={dateFields.map((f) => ({ value: f.key, label: f.label }))}
                  placeholder="Start date..."
                  triggerClassName="h-9 bg-surface text-xs"
                />
              </div>
              <div className="w-44">
                <span className="text-[10px] font-bold text-on-surface-variant uppercase block mb-1">To</span>
                <AppDropdown
                  value={panelFieldB}
                  onChange={setPanelFieldB}
                  options={dateFields.map((f) => ({ value: f.key, label: f.label }))}
                  placeholder="End date..."
                  triggerClassName="h-9 bg-surface text-xs"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-1.5 pb-0.5">
            <button
              type="button"
              onClick={confirmPanel}
              disabled={!panelFieldA || (activePanel === 'days-between' && !panelFieldB)}
              className="h-9 px-3 rounded-[8px] bg-primary text-on-primary text-xs font-bold disabled:opacity-40"
            >
              {editingIndex !== null ? 'Update' : 'Insert'}
            </button>
            <button
              type="button"
              onClick={closePanel}
              className="h-9 px-3 rounded-[8px] text-xs font-bold text-on-surface-variant hover:bg-surface-container"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Step6Calculations({
  calculations = [],
  setCalculations,
  calculationTemplates = [],
  allFields = [],
}) {
  function applyTemplate(tmpl) {
    const key = `calc_${Date.now()}`
    setCalculations((prev) => [
      ...prev,
      {
        key,
        label: tmpl.label.split('=')[0].trim(),
        expression: tmpl.expression,
        format: tmpl.dataType || 'number',
      },
    ])
  }

  function addBlankCalculation() {
    const key = `calc_${Date.now()}`
    setCalculations((prev) => [
      ...prev,
      {
        key,
        label: 'New Formula Column',
        expression: '',
        format: 'number',
      },
    ])
  }

  function removeCalculation(key) {
    setCalculations((prev) => prev.filter((c) => c.key !== key))
  }

  function updateCalculation(key, prop, value) {
    setCalculations((prev) => prev.map((c) => (c.key === key ? { ...c, [prop]: value } : c)))
  }

  return (
    <div className="space-y-3 max-w-4xl mx-auto py-1">
      <StepHeader
        title="No-Code Calculated Columns"
        subtitle="Build derived metrics by clicking fields and operators — no formula syntax to remember."
        actions={
          <button
            type="button"
            onClick={addBlankCalculation}
            className="h-9 text-xs md:text-sm font-bold text-primary border border-primary/40 bg-primary/5 px-3.5 rounded-[8px] hover:bg-primary/10 flex items-center gap-1.5 transition-colors shadow-xs"
          >
            <span className="material-symbols-outlined text-base">add</span>
            Custom Formula
          </button>
        }
      />

      {/* Preset Quick-Add Templates */}
      <StepCard title="Popular Formula Templates" subtitle="Click any template to quickly configure standard business metrics.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {calculationTemplates.map((tmpl) => (
            <button
              key={tmpl.id}
              type="button"
              onClick={() => applyTemplate(tmpl)}
              className="flex items-center justify-between p-2.5 rounded-[8px] border border-surface-container-high bg-surface hover:bg-surface-container transition-colors text-left"
            >
              <div className="min-w-0 pr-1">
                <h5 className="text-xs font-bold text-on-surface truncate">{tmpl.label}</h5>
                <span className="text-[10px] text-on-surface-variant block truncate mt-0.5">Click to add &amp; customize</span>
              </div>
              <span className="material-symbols-outlined text-primary text-base shrink-0">add_circle</span>
            </button>
          ))}
        </div>
      </StepCard>

      {/* Formula Builder */}
      <StepCard title={`Configured Formulas (${calculations.length})`}>
        {calculations.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-surface-container-high rounded-[8px] text-xs text-on-surface-variant">
            No calculated columns configured. Click a template above or create a custom formula.
          </div>
        ) : (
          <div className="space-y-3">
            {calculations.map((calc) => (
              <div key={calc.key} className="p-3 bg-surface rounded-[8px] border border-surface-container-high space-y-2.5">
                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div>
                      <label className={LABEL_CLASS}>Column Label</label>
                      <input
                        type="text"
                        value={calc.label}
                        onChange={(e) => updateCalculation(calc.key, 'label', e.target.value)}
                        placeholder="e.g., Gross Profit Margin"
                        className={`${INPUT_CLASS} font-semibold`}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS}>Display Format</label>
                      <AppDropdown
                        value={calc.format}
                        onChange={(val) => updateCalculation(calc.key, 'format', val)}
                        options={CALC_FORMAT_OPTIONS}
                        triggerClassName={DROPDOWN_TRIGGER_CLASS}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCalculation(calc.key)}
                    className="p-2 text-slate-400 hover:text-error hover:bg-surface-container rounded-[8px] self-start transition-colors"
                    title="Remove Formula Column"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>

                <FormulaBuilder
                  calc={calc}
                  allFields={allFields}
                  onChangeExpression={(expr) => updateCalculation(calc.key, 'expression', expr)}
                />
              </div>
            ))}
          </div>
        )}
      </StepCard>
    </div>
  )
}
